/*
 * Copyright (c) 2014-2023 Comparatio USA  LLC. <https://www.comparatio.com>
 */

import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {MatDialog} from '@angular/material';
import {ConfirmationService} from 'primeng/api';
import {ToastrService} from 'ngx-toastr';
import {MasterDataService} from '../../services/master-data.service';
import {MasterCustomerService} from '../../services/master-customer.service';
import {PlantService} from '../../services/plant.service';
import {RackService} from '../../services/rack.service';
import {IMasterData} from '../../interfaces/IMasterData';
import {permissions} from '../../../../permissions';
import {Title} from '@angular/platform-browser';
import {Subscription} from 'rxjs/Subscription';
import {ScannedDataService} from '../../services/scanned-data.service';
import {fiveDecCompanies} from '../../../../../environments/environment';

@Component({
    selector: 'app-master-data-form',
    templateUrl: './master-data-form.component.html',
    providers: [
        MasterDataService,
        ConfirmationService,
    ],
    preserveWhitespaces: true
})
export class MasterDataFormComponent implements OnInit, OnDestroy {
    public permissions = permissions;

    public errors: Array<any> = [];
    public action: String = '';
    public editMode: boolean;
    public returnEditToView: boolean;

    public loading = false;
    public record: IMasterData;
    public allowInventoryManagement: boolean;

    public companies: Array<any>;
    private customers: Array<any>;
    public filteredCustomers: Array<any>;
    public racks: Array<any>;

    public MD1_ID: number;
    public sdOptions: Array<any>;
    private subscriptions: Array<Subscription>;
    private scannedDataServiceRef = ScannedDataService;

    public readonly fiveDecCompanies = fiveDecCompanies;
    public readonly parseInt = parseInt;

    constructor(
        private route: ActivatedRoute,
        private masterDataService: MasterDataService,
        private customerService: MasterCustomerService,
        private plantService: PlantService,
        private rackService: RackService,
        private confirmationService: ConfirmationService,
        private router: Router,
        private toast: ToastrService,
        public  dialog: MatDialog,
        private title: Title
    ) {
        this.title.setTitle('OES - Master Data');
    }

    ngOnInit() {
        this.action = this.route.snapshot.url[2].toString();
        this.MD1_ID = (this.route.snapshot.params.id !== undefined && this.route.snapshot.params.id !== null)
            ? this.route.snapshot.params.id : 0;
        this.editMode = this.action !== 'view';
        this.returnEditToView = this.action === 'view';
        this.record = this.masterDataService.initialize();
        this.allowInventoryManagement = false;

        this.companies = [];
        this.customers = [];
        this.racks = [];

        this.sdOptions = [];
        this.subscriptions = [];

        this.getData();
    }

    ngOnDestroy() {
        for (let index = 0; index < this.subscriptions.length; index++) {
            this.subscriptions[index].unsubscribe();
        }
    }

    getData() {
        this.loading = true;
        this.subscriptions.push(
            this.masterDataService.preload().subscribe(result => {
                if (result.success) {
                    this.companies = result.data.companies.map(company => {
                        return {
                            CO1_ID: Number(company.CO1_ID),
                            CO1_NAME: company.CO1_NAME
                        }
                    });
                    this.customers = result.data.customers.map(customer => {
                        return {
                            CU1_ID: Number(customer.CU1_ID),
                            CU1_NAME: customer.CU1_NAME,
                            CO1_ID: Number(customer.CO1_ID)
                        }
                    });
                    this.filteredCustomers = this.customers.slice();
                    this.racks = result.data.racks.map(rack => {
                        return {
                            RA1_ID: Number(rack.RA1_ID),
                            RA1_SHIP_TO_ID: rack.RA1_SHIP_TO_ID,
                            RA1_SHIP_TO_NAME: rack.RA1_SHIP_TO_NAME
                        }
                    });

                    this.loadPartInfo();
                } else {
                    this.toast.error('Unable to load required data for form fields. Please try later!', 'Error');
                    setTimeout(() => {
                        this.loading = false;
                        this.backClick();
                    }, 2000);
                    this.loading = false;
                }
            }, () => {
                this.toast.error('Request timed out', 'Service Error');
                this.loading = false;
            })
        );
    }

    private loadPartInfo() {
        // if (this.route.snapshot.params.id !== undefined && this.route.snapshot.params.id !== null) {
        if (this.MD1_ID > 0) {
            this.subscriptions.push(
                this.masterDataService.getById(this.MD1_ID).subscribe(masterDataResult => {
                    if (masterDataResult.success) {
                        this.record = masterDataResult.data;
                        this.record.CO1_ID = Number(masterDataResult.data.CO1_ID);
                        this.record.CU1_ID = Number(masterDataResult.data.CU1_ID);
                        this.record.RA1_ID = Number(masterDataResult.data.RA1_ID);
                        this.allowInventoryManagement = Number(masterDataResult.masterCustomer.CU0_ALLOW_INVENTORY_MANAGEMENT) === 1;
                        this.setScannedDataOptions();
                        this.loading = false;
                    } else {
                        this.toast.error('Unable to load data. Please try later!', 'Error');
                        setTimeout(() => {
                            this.loading = false;
                            this.backClick();
                        }, 2000);
                        this.loading = false;
                    }
                }, () => {
                    this.loading = false;
                    this.toast.error('Request timed out', 'Service Error');
                })
            );
        } else {
            this.loading = false;
        }
    }

    private setScannedDataOptions() {
        this.sdOptions = [
            {
                id: this.scannedDataServiceRef.LABEL_SD1.replace(' ', ''),
                label: this.scannedDataServiceRef.LABEL_SD1,
                mirrorUrl: this.scannedDataServiceRef.MIRROR_URL_SD1
            }
        ];
        if (this.allowInventoryManagement) {
            this.sdOptions.push({
                id: this.scannedDataServiceRef.LABEL_SD2.replace(' ', ''),
                label: this.scannedDataServiceRef.LABEL_SD2,
                mirrorUrl: this.scannedDataServiceRef.MIRROR_URL_SD2
            });
            this.sdOptions.push({
                id: this.scannedDataServiceRef.LABEL_SD3.replace(' ', ''),
                label: this.scannedDataServiceRef.LABEL_SD3,
                mirrorUrl: this.scannedDataServiceRef.MIRROR_URL_SD3
            });
        }
    }

    backClick() {
        this.loading = false;
        window.history.back();
    }

    cancelClick() {
        if (this.action === 'add' || (this.action === 'edit' && !this.returnEditToView)) {
            this.backClick();
        } else {
            this.editMode = false;
            this.action = 'view';
            this.getData();
        }
    }

    companySelectionChanged() {
        if (this.record.CO1_ID !== null) {
            this.filteredCustomers = this.customers.slice().filter(customer => Number(customer.CO1_ID) === Number(this.record.CO1_ID));
            this.record.CU1_ID = null;
        }
    }

    editRecord() {
        this.editMode = true;
        this.action = 'edit';
    }

    saveClick() {
        this.loading = true;
        this.masterDataService.save(this.record, this.action === 'add').subscribe(result => {
            if (result.success) {
                this.toast.success('Master data record ' + (this.action === 'add' ? 'created' : 'updated') + '!', 'Success');
                // this.router.navigate(['order-entry-system/master-data']);
                this.cancelClick();
            } else {
                this.errors = result.errors;
            }
            this.loading = false;
        }, () => {
            this.toast.error('Request timed out', 'Service Error');
            this.loading = false;
        });
    }
}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/views/oes/master-data/master-data-form/master-data-form.component.ts